import type { ImportHistoryEntry } from "@/api/researchCms.service";
import { practiceAreaLabel } from "./bpaImport";

export type ArchivedBannerTarget = { label: string; editionId: string; bpaId: string };

export type ArchivedBannerModel = {
  lead: string;
  targets: ArchivedBannerTarget[];
  createdAt: string;
};

function newestFirst(a: ImportHistoryEntry, b: ImportHistoryEntry): number {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

function isSplitOrMerge(entry: ImportHistoryEntry): boolean {
  return entry.purpose === "split" || entry.purpose === "merge";
}

function targetOf(entry: ImportHistoryEntry, editionId: string): ArchivedBannerTarget {
  return {
    label: practiceAreaLabel(
      {
        name: entry.targetBpaName,
        editionId: entry.targetEditionId,
        editionName: entry.targetEditionName,
        publicationName: entry.targetPublicationName,
      },
      editionId,
    ),
    editionId: entry.targetEditionId,
    bpaId: entry.targetBpaId,
  };
}

function uniqueTargets(entries: ImportHistoryEntry[], editionId: string): ArchivedBannerTarget[] {
  const seen = new Set<string>();
  const targets: ArchivedBannerTarget[] = [];
  for (const entry of entries) {
    if (seen.has(entry.targetEditionBpaId)) continue;
    seen.add(entry.targetEditionBpaId);
    targets.push(targetOf(entry, editionId));
  }
  return targets;
}

export function archivedBannerModel(
  history: ImportHistoryEntry[],
  editionBpaId: string,
  editionId: string,
): ArchivedBannerModel | null {
  const outgoing = history
    .filter(entry => entry.sourceEditionBpaId === editionBpaId)
    .sort(newestFirst);
  const newest = outgoing.find(isSplitOrMerge) ?? outgoing[0];
  if (!newest) return null;
  if (newest.purpose === "split") {
    const splits = outgoing.filter(entry => entry.purpose === "split");
    return {
      lead: "Split into",
      targets: uniqueTargets(splits, editionId),
      createdAt: newest.createdAt,
    };
  }
  return {
    lead: newest.purpose === "merge" ? "Merged into" : "Its content was imported into",
    targets: [targetOf(newest, editionId)],
    createdAt: newest.createdAt,
  };
}
